import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ShoppingCart,
  BarChart2,
  Settings,
  Package,
  ChevronDown,
  ChevronRight,
  LogOut,
  Key,
  HelpCircle,
  X,
  Building2,
  FileBarChart,
  UserCircle,
  Boxes,
  Tags,
  Truck,
  FileText,
  ClipboardList,
  Receipt,
  UserCog,
  Building,
  Briefcase,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import ChangePasswordModal from "./ChangePasswordModal";
import { motion, AnimatePresence } from "framer-motion";

// Menu item component with modern styling
const MenuItem = ({
  item,
  isActive,
  isExpanded,
  onToggle,
  depth = 0,
  currentPath,
  isCollapsed,
}) => {
  const hasChildren = item.items && item.items.length > 0;

  if (hasChildren) {
    return (
      <div className="mb-0.5">
        <button
          onClick={onToggle}
          className={`group w-full flex items-center justify-between px-3 py-2.5 text-[13px] font-medium transition-all duration-200 rounded-xl mx-auto
            ${
              isActive || isExpanded
                ? "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 dark:from-blue-900/40 dark:to-indigo-900/40 dark:text-blue-300"
                : "text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800/50"
            }
            ${isCollapsed ? "justify-center w-12 mx-auto" : "w-full"}`}
          title={isCollapsed ? item.label : undefined}
        >
          <div className="flex items-center gap-3">
            {item.icon && (
              <item.icon
                className={`w-5 h-5 flex-shrink-0 transition-colors duration-200 ${
                  isActive || isExpanded
                    ? "text-blue-600 dark:text-blue-400"
                    : "text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300"
                }`}
              />
            )}
            {!isCollapsed && <span>{item.label}</span>}
          </div>
          {!isCollapsed && (
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown
                className={`w-4 h-4 transition-colors duration-200 ${
                  isActive || isExpanded ? "text-blue-500" : "text-gray-400"
                }`}
              />
            </motion.div>
          )}
        </button>

        <AnimatePresence initial={false}>
          {isExpanded && !isCollapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="overflow-hidden"
            >
              <div className="mt-1 ml-4 pl-4 border-l-2 border-gray-100 dark:border-gray-700 space-y-0.5">
                {item.items.map((subItem) => (
                  <MenuItem
                    key={subItem.id}
                    item={subItem}
                    isActive={currentPath && currentPath.includes(subItem.path)}
                    depth={depth + 1}
                    currentPath={currentPath}
                    isCollapsed={isCollapsed}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <Link
      to={`/admin${item.path}`}
      className={`group flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium transition-all duration-200 rounded-xl mb-0.5
        ${
          isActive
            ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
            : "text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800/50"
        }
        ${isCollapsed ? "justify-center w-12 mx-auto" : "w-full"}
        ${depth > 0 ? "py-2" : ""}`}
      title={isCollapsed ? item.label : undefined}
    >
      {item.icon && (
        <item.icon
          className={`w-5 h-5 flex-shrink-0 transition-colors duration-200 ${
            isActive
              ? "text-white"
              : "text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300"
          }`}
        />
      )}
      {!isCollapsed && <span>{item.label}</span>}
      {isActive && !isCollapsed && (
        <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full" />
      )}
    </Link>
  );
};

export default function Sidebar({ isOpen, onClose, isCollapsed = false }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const handleLogout = () => {
    logout();
  };

  const menuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      path: "/dashboard",
      icon: LayoutDashboard,
    },
    {
      id: "sales",
      label: "Sales",
      icon: ShoppingCart,
      items: [
        {
          id: "customers",
          label: "Customers",
          path: "/customers",
          icon: Users,
        },
        {
          id: "orders",
          label: "Purchase Orders",
          path: "/orders",
          icon: ClipboardList,
        },
        {
          id: "delivery-orders",
          label: "Delivery Orders",
          path: "/delivery-orders",
          icon: Truck,
        },
        { id: "invoices", label: "Invoices", path: "/invoices", icon: Receipt },
        { id: "crm", label: "CRM", path: "/crm", icon: Briefcase },
      ],
    },
    {
      id: "catalog",
      label: "Catalog",
      icon: Package,
      items: [
        { id: "products", label: "Products", path: "/products", icon: Boxes },
        {
          id: "inventory",
          label: "Inventory",
          path: "/inventory",
          icon: Package,
        },
        {
          id: "categories",
          label: "Categories",
          path: "/categories",
          icon: Tags,
        },
        {
          id: "suppliers",
          label: "Suppliers",
          path: "/suppliers",
          icon: Truck,
        },
      ],
    },
    {
      id: "reports",
      label: "Reports",
      icon: BarChart2,
      items: [
        {
          id: "reports-main",
          label: "General Reports",
          path: "/reports",
          icon: FileBarChart,
        },
        {
          id: "inventory-reports",
          label: "Inventory Reports",
          path: "/inventory-reports",
          icon: FileText,
        },
      ],
    },
    {
      id: "admin",
      label: "Administration",
      icon: Settings,
      items: [
        {
          id: "users",
          label: "User Management",
          path: "/users",
          icon: UserCog,
        },
        {
          id: "company-setup",
          label: "Company Setup",
          path: "/company-setup",
          icon: Building,
        },
        {
          id: "company-administration",
          label: "Company Admin",
          path: "/company-administration",
          icon: Building2,
        },
      ],
    },
  ];

  // Initialize expanded menu based on current path
  const [expandedMenu, setExpandedMenu] = useState(() => {
    const activeItem = menuItems.find(
      (item) =>
        item.items &&
        item.items.some((subItem) => location.pathname.includes(subItem.path))
    );
    return activeItem ? activeItem.id : null;
  });

  const toggleMenu = (menuId) => {
    setExpandedMenu((prev) => (prev === menuId ? null : menuId));
  };

  const isGroupActive = (item) => {
    if (!item.items) return false;
    return item.items.some((subItem) =>
      location.pathname.includes(subItem.path)
    );
  };

  const isLinkActive = (path) => {
    return location.pathname.includes(path);
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-40 lg:hidden transition-all duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Sidebar Container */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-white dark:bg-gray-900 border-r border-gray-200/80 dark:border-gray-800 transform transition-all duration-300 ease-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          ${isCollapsed ? "w-20" : "w-72"}`}
      >
        {/* Logo Area */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-900" />
            </div>
            {!isCollapsed && (
              <div>
                <h1 className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                  Axia B2B
                </h1>
                <p className="text-[10px] text-gray-400 font-medium tracking-wide uppercase">
                  Business Suite
                </p>
              </div>
            )}
          </div>

          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-6 px-3 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
          {!isCollapsed && (
            <div className="px-3 mb-4">
              <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                Main Menu
              </span>
            </div>
          )}

          <nav className="space-y-1">
            {menuItems.map((item) => {
              const isItemActive = !item.items && isLinkActive(item.path);
              const isExpanded = expandedMenu === item.id;
              const isGroupActiveState = isGroupActive(item);

              return (
                <MenuItem
                  key={item.id}
                  item={item}
                  isActive={item.items ? isGroupActiveState : isItemActive}
                  isExpanded={isExpanded}
                  onToggle={() => toggleMenu(item.id)}
                  currentPath={location.pathname}
                  isCollapsed={isCollapsed}
                />
              );
            })}
          </nav>
        </div>

        {/* User Profile Footer */}
        <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="relative">
            <button
              onClick={() =>
                !isCollapsed && setShowProfileMenu(!showProfileMenu)
              }
              className={`w-full flex items-center justify-between rounded-xl transition-all duration-200 ${
                isCollapsed
                  ? "justify-center"
                  : "hover:bg-gray-100 dark:hover:bg-gray-700/50 p-2 -m-2 cursor-pointer"
              }`}
            >
              <div
                className={`flex items-center ${
                  isCollapsed ? "justify-center w-full" : ""
                }`}
              >
                <div className="relative">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-blue-500/25">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800" />
                </div>

                {!isCollapsed && (
                  <div className="ml-3 min-w-0 flex-1 text-left">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {user?.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate capitalize">
                      {user?.role?.replace("_", " ")}
                    </p>
                  </div>
                )}
              </div>

              {!isCollapsed && (
                <ChevronRight
                  className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                    showProfileMenu ? "rotate-90" : ""
                  }`}
                />
              )}
            </button>

            {!isCollapsed && (
              <AnimatePresence>
                {showProfileMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-[60]"
                      onClick={() => setShowProfileMenu(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute bottom-full right-0 mb-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-[70] overflow-hidden"
                    >
                      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {user?.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {user?.email || "admin@ennovace.com"}
                        </p>
                      </div>

                      <div className="py-1">
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            setShowChangePasswordModal(true);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-3 transition-colors"
                        >
                          <Key className="w-4 h-4 text-gray-400" />
                          Change Password
                        </button>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-3 transition-colors"
                        >
                          <UserCircle className="w-4 h-4 text-gray-400" />
                          View Profile
                        </button>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-3 transition-colors"
                        >
                          <HelpCircle className="w-4 h-4 text-gray-400" />
                          Help Center
                        </button>
                      </div>

                      <div className="border-t border-gray-100 dark:border-gray-700 pt-1 mt-1">
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            handleLogout();
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            )}
          </div>
        </div>
      </aside>

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </>
  );
}
